import React, { forwardRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'datePickerVariant',
})(({ theme, datePickerVariant }) => {
  const variants = {
    default: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 0,
      },
    },
    auth: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${theme.palette.grey[300]}`,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused': {
          backgroundColor: 'white',
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
        },
      },
    },
    form: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: 'white',
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        },
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
            borderColor: theme.palette.primary.main,
          },
        },
      },
    },
    compact: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius / 2,
        '& .MuiOutlinedInput-input': {
          padding: theme.spacing(1),
        },
      },
    },
    rounded: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 2,
      },
    },
  };

  return variants[datePickerVariant] || variants.default;
});

/**
 * EasyCheckDatePicker - Standardized date picker component with dd-MM-yyyy format
 * 
 * Features:
 * - Multiple visual variants
 * - dd-MM-yyyy display format
 * - Consistent theming
 * - Full Material-UI DatePicker compatibility
 */
const EasyCheckDatePicker = forwardRef(({
  datePickerVariant = 'default',
  format = 'dd-MM-yyyy',
  label,
  value,
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  slotProps = {},
  sx,
  ...props
}, ref) => {
  
  // Merge default textField props with provided ones
  const defaultTextFieldProps = {
    fullWidth,
    error,
    helperText,
    required,
    disabled,
    sx,
  };

  const mergedSlotProps = {
    ...slotProps,
    textField: {
      ...defaultTextFieldProps,
      ...slotProps.textField,
    },
  };

  return (
    <StyledDatePicker
      ref={ref}
      datePickerVariant={datePickerVariant}
      format={format}
      label={label}
      value={value}
      onChange={onChange}
      slotProps={mergedSlotProps}
      {...props}
    />
  );
});

EasyCheckDatePicker.propTypes = {
  datePickerVariant: PropTypes.oneOf(['default', 'auth', 'form', 'compact', 'rounded']),
  format: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  slotProps: PropTypes.object,
  sx: PropTypes.object,
};

EasyCheckDatePicker.displayName = 'EasyCheckDatePicker';

export default EasyCheckDatePicker; 